import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, CheckCheck, Loader2, Pencil, RefreshCw, Search, WifiOff, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { WM_TIMEOUT_MS, wmMessage } from "@/lib/wmError";
import { sanitizeQty } from "@/lib/wmDigits";
import { fmt, fmtCost, hasKey, isRawItem } from "@/components/waste-meals/wmUtils";
import { RecipePickerDialog } from "@/components/waste-meals/admin/RecipePickerDialog";

const C = OPS.wm.admin;
const FILTERS = ["all", "review", "unlinked", "linked"];
/** SPEC §2.4: the two سبانخ rows are suggestions only — never linked in bulk
 *  (the server sends its own list as `never_auto_link`; this is the fallback). */
const NEVER_AUTO = ["022", "023"];

const nameOf = (o, lang) => (lang === "ar" ? o?.name_ar || o?.name_en : o?.name_en || o?.name_ar) || "";
const isLinked = (row) => !!row?.link?.recipe_key;
const isDangling = (row) => isLinked(row) && !!row.link.recipe_missing;
const confidence = (row) => row?.suggestion?.confidence || "none";
const needsFactor = (item) => !!item && item.unit_key !== "piece";
const posNum = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null; };
/** A weighed product (002 عجين) linked to its per-piece POS recipe without the
 *  POS pieces per kilo — what a bulk confirm leaves: no cost until it is set. */
const factorSuspect = (row) => isLinked(row)
    && (row.link.needs_factor === true || (needsFactor(row.item) && !posNum(row.link.qty_factor)));

/** One of the four filter buckets; stock items are costed from the count, never linked. */
function inFilter(row, f) {
    if (f === "all") return true;
    if (isRawItem(row.item)) return false;
    if (f === "linked") return isLinked(row);
    if (f === "unlinked") return !isLinked(row);
    // review: what an admin must decide by hand
    return isDangling(row) || (!isLinked(row) && confidence(row) !== "confirmed") || factorSuspect(row);
}

const eligibleForBulk = (row, never) => !isRawItem(row.item) && !isLinked(row)
    && confidence(row) === "confirmed" && !!row.suggestion?.recipe_key && !never.has(row.item?.code);

function StateBadge({ row }) {
    const { t } = useI18n();
    const id = row.item.id;
    let variant = "secondary"; let key = "wm_links_confidence_none"; let state = "none";
    if (isRawItem(row.item)) { variant = "brown"; key = "wm_raw_badge"; state = "stock"; }
    else if (isDangling(row)) { variant = "destructive"; key = "wm_links_state_dangling"; state = "dangling"; }
    else if (isLinked(row)) { variant = "success"; key = "wm_links_state_linked"; state = "linked"; }
    else if (confidence(row) === "confirmed") { variant = "default"; key = "wm_links_confidence_confirmed"; state = "confirmed"; }
    else if (confidence(row) === "uncertain") { variant = "warning"; key = "wm_links_confidence_uncertain"; state = "uncertain"; }
    return (
        <Badge data-testid={C.linkConfidence(id)} data-state={state} variant={variant}
               className="shrink-0 px-2 py-0.5 text-[10px]">
            {t(key)}
        </Badge>
    );
}

/** One item: its suggestion or link, the recipe picker, the factor, confirm. */
function LinkRow({ row, pool, costsVisible, onSaved }) {
    const { t, lang } = useI18n();
    const { item, link, suggestion } = row;
    const id = item.id;
    const linked = isLinked(row);
    const dangling = isDangling(row);
    const raw = isRawItem(item);
    const factorShown = needsFactor(item);
    const unitName = (lang === "ar" ? item.unit_name_ar : item.unit_name_en) || item.unit_name_ar || item.unit_key;

    const start = linked
        ? { recipe_key: link.recipe_key, recipe_name: link.recipe_name }
        : (suggestion?.recipe_key ? { recipe_key: suggestion.recipe_key, recipe_name: suggestion.recipe_name } : null);
    const [pick, setPick] = useState(start);
    const [factor, setFactor] = useState(linked && posNum(link.qty_factor) ? String(link.qty_factor) : "");
    const [picker, setPicker] = useState(false);
    const [busy, setBusy] = useState(false);

    const suggested = useMemo(() => {
        const out = []; const seen = new Set();
        for (const r of [suggestion?.recipe_key ? suggestion : null, ...(suggestion?.alternatives || []),
                         linked ? link : null]) {
            if (r?.recipe_key && !seen.has(r.recipe_key)) {
                seen.add(r.recipe_key); out.push({ recipe_key: r.recipe_key, recipe_name: r.recipe_name });
            }
        }
        return out;
    }, [suggestion, link, linked]);

    const factorVal = factorShown ? posNum(factor) : (posNum(link?.qty_factor) || 1);
    const dirty = !linked ? !!pick
        : (pick?.recipe_key !== link.recipe_key || (factorShown && factorVal !== posNum(link.qty_factor)));
    const canSave = !!pick && !!factorVal && dirty && !busy;

    const put = async (body, okKey) => {
        setBusy(true);
        try {
            const res = await api.put(`/waste-meals/admin/recipe-links/${id}`, body, { timeout: WM_TIMEOUT_MS });
            toast.success(t(okKey));
            await onSaved(res.data);
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };
    // a piece item's factor is 1 by definition: only a weighed item sends one
    const save = () => put(factorShown ? { recipe_key: pick.recipe_key, qty_factor: factorVal }
        : { recipe_key: pick.recipe_key }, "wm_links_saved");
    const unlink = () => {
        if (!window.confirm(t("wm_links_unlink_confirm"))) return;
        put({ recipe_key: null }, "wm_links_unlinked");
    };

    // the server sends `unit_cost_sar` to everyone (null when hidden): `costs_visible` decides
    const costKnown = costsVisible && hasKey(row, "unit_cost_sar");
    const zero = Number(row.zero_cost_lines) || 0;
    const sarLabel = lang === "ar" ? "ر.س" : "SAR";
    // `linked_by` is `{id, name}` (sc_core.actor_ref; name may be ""): never
    // hand the object itself to React.
    const linkedBy = typeof link?.linked_by === "string" ? link.linked_by : link?.linked_by?.name || "";

    return (
        <li data-testid={C.linkRow(id)} data-linked={linked ? "true" : "false"}
            className={`space-y-2 rounded-[var(--radius-lg)] border bg-[color:var(--bg-surface)] p-3 ${
                dangling ? "border-[color:var(--danger)]/50" : "border-[color:var(--border-soft)]"}`}>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-snug text-[color:var(--ink)]">
                        {item.code ? (
                            <bdi dir="ltr" className="me-1.5 text-[11px] font-medium tabular-nums text-[color:var(--ink-faint)]">{item.code}</bdi>
                        ) : null}
                        {nameOf(item, lang)}
                    </p>
                    <p className="text-[11px] text-[color:var(--ink-faint)]">
                        {unitName}
                        {item.is_active === false ? ` · ${t("wm_badge_inactive_item")}` : ""}
                    </p>
                </div>
                <StateBadge row={row} />
            </div>

            {raw ? (
                <p className="text-[11px] leading-relaxed text-[color:var(--ink-muted)]">
                    {t("wm_links_stock_item")}
                    {item.sc_item_code ? <> · <bdi dir="ltr">{item.sc_item_code}</bdi></> : null}
                </p>
            ) : (
                <>
                    <button type="button" data-testid={C.linkRecipe(id)} onClick={() => setPicker(true)} disabled={busy}
                            className="flex w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-3 py-2 text-start transition-colors hover:border-[color:var(--brand-orange)] disabled:opacity-60">
                        <span className="min-w-0">
                            <span className="block text-[10px] font-bold text-[color:var(--ink-faint)]">
                                {linked && !dirty ? t("wm_links_linked_to") : t("wm_links_suggestion")}
                            </span>
                            <bdi className={`block truncate text-sm font-semibold ${pick ? "text-[color:var(--ink)]" : "text-[color:var(--ink-faint)]"}`}>
                                {pick ? pick.recipe_name || pick.recipe_key : t("wm_links_pick")}
                            </bdi>
                        </span>
                        <Pencil className="h-3.5 w-3.5 shrink-0 text-[color:var(--brand-brown)]" />
                    </button>

                    {dangling ? (
                        <p className="flex items-start gap-1.5 text-[11px] font-semibold text-[color:var(--danger)]">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t("wm_links_recipe_missing")}
                        </p>
                    ) : null}
                    {factorSuspect(row) && !dirty ? (
                        <p className="text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">{t("wm_links_factor_check")}</p>
                    ) : null}
                    {!linked && confidence(row) !== "confirmed" && pick ? (
                        <p className="text-[11px] text-[color:var(--warning-soft-fg)]">{t("wm_links_check_suggestion")}</p>
                    ) : null}

                    {factorShown ? (
                        <div className="space-y-1">
                            <div className="flex items-center justify-between gap-2">
                                <label htmlFor={`wm-link-factor-${id}`} className="text-[11px] font-semibold text-[color:var(--ink)]">
                                    {fmt(t("wm_links_factor"), { unit: unitName })}
                                </label>
                                <div className="w-28 shrink-0" dir="ltr">
                                    <Input id={`wm-link-factor-${id}`} data-testid={C.linkFactor(id)} value={factor}
                                           onChange={(e) => setFactor(sanitizeQty(e.target.value, true))}
                                           inputMode="decimal" dir="ltr" placeholder="0" autoComplete="off"
                                           className={`h-10 text-base font-semibold tabular-nums ${
                                               pick && !posNum(factor) ? "border-[color:var(--warning)]" : ""}`} />
                                </div>
                            </div>
                            <p className="text-[10px] leading-relaxed text-[color:var(--ink-faint)]">
                                {fmt(t("wm_links_factor_hint"), { unit: unitName })}
                            </p>
                        </div>
                    ) : null}

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        {costKnown ? (
                            <span data-testid={C.linkCost(id)} className="text-[11px] font-semibold text-[color:var(--ink)]">
                                {t("wm_links_unit_cost")}:{" "}
                                {row.unit_cost_sar === null || row.unit_cost_sar === undefined
                                    ? <span className="text-[color:var(--ink-faint)]">—</span>
                                    : <bdi dir="ltr">{fmtCost(row.unit_cost_sar, lang)} {sarLabel}</bdi>}
                                <span className="text-[color:var(--ink-faint)]"> / {unitName}</span>
                            </span>
                        ) : null}
                        {zero > 0 ? (
                            <span className="text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                                {fmt(t("wm_links_zero_lines"), {
                                    n: new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(zero),
                                })}
                            </span>
                        ) : null}
                        {linked && linkedBy ? (
                            <span className="text-[10px] text-[color:var(--ink-faint)]">
                                {t("wm_by")} {linkedBy}
                                {link.linked_at ? <> · <bdi dir="ltr">{String(link.linked_at).slice(0, 10)}</bdi></> : null}
                            </span>
                        ) : null}
                        <div className="ms-auto flex gap-1.5">
                            {linked ? (
                                <Button type="button" size="sm" variant="ghost" data-testid={C.linkUnlink(id)}
                                        onClick={unlink} disabled={busy}
                                        className="h-9 gap-1 px-2.5 text-[11px] text-[color:var(--danger)]">
                                    <X className="h-3.5 w-3.5" />{t("wm_links_unlink")}
                                </Button>
                            ) : null}
                            {!linked || dirty ? (
                                <Button type="button" size="sm" data-testid={C.linkConfirm(id)} onClick={save}
                                        disabled={!canSave} className="h-9 gap-1 px-3 text-xs font-bold">
                                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                    {linked ? t("wm_links_save") : t("wm_links_confirm")}
                                </Button>
                            ) : null}
                        </div>
                    </div>
                    {pick && factorShown && !posNum(factor) ? (
                        <p className="text-[10px] font-semibold text-[color:var(--warning-soft-fg)]">{t("wm_links_factor_required")}</p>
                    ) : null}
                </>
            )}

            {!raw ? (
                <RecipePickerDialog open={picker} onOpenChange={setPicker} itemName={nameOf(item, lang)}
                                    suggested={suggested} pool={pool} current={pick?.recipe_key}
                                    onPick={(r) => { setPick(r); setPicker(false); }} />
            ) : null}
        </li>
    );
}

/**
 * «ربط الوصفات» (D5) — link each W&M item to its POS recipe so a record
 * snapshots its cost when it is saved. Every link is the admin's own
 * confirmation: one tap per row, or «تأكيد كل المطابقات المؤكدة» for the exact
 * name matches only (the two سبانخ rows are never in it). An item counted in
 * kilos (002 عجين) needs the POS pieces per kilo before it can be linked.
 * Cost figures appear only when the server sends them (report viewers and
 * `recipes_read` holders) — a plain `wm_admin` sees names and confidence.
 */
export function RecipeLinksAdmin() {
    const { t, lang } = useI18n();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [meta, setMeta] = useState({ costsVisible: false, recipesN: null, never: NEVER_AUTO });
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const r = await api.get("/waste-meals/admin/recipe-links");
            const d = r.data || {};
            const list = Array.isArray(d) ? d : d.rows || [];
            setRows(list);
            setMeta({
                // older shape: cost keys present and non-null means they were allowed
                costsVisible: typeof d.costs_visible === "boolean" ? d.costs_visible
                    : list.some((x) => x.unit_cost_sar !== null && x.unit_cost_sar !== undefined),
                recipesN: typeof d.recipes_n === "number" ? d.recipes_n : null,
                never: Array.isArray(d.never_auto_link) ? d.never_auto_link : NEVER_AUTO,
            });
        } catch (e) {
            const msg = wmMessage(e, lang);
            if (msg) toast.error(msg); else setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [lang]);

    useEffect(() => { load(); }, [load]);

    // A PUT that answers with the row in the list's shape updates it in place;
    // anything else re-reads the list (suggestions are computed server-side).
    const onSaved = useCallback(async (data) => {
        if (data?.item?.id) setRows((rs) => rs.map((r) => (r.item?.id === data.item.id ? data : r)));
        else await load();
    }, [load]);

    // Every recipe the matcher proposed anywhere: the picker's searchable pool.
    const pool = useMemo(() => {
        const m = new Map();
        const add = (r) => { if (r?.recipe_key && !m.has(r.recipe_key)) m.set(r.recipe_key, { recipe_key: r.recipe_key, recipe_name: r.recipe_name }); };
        for (const row of rows) {
            add(row.suggestion);
            (row.suggestion?.alternatives || []).forEach(add);
            add(row.link);
        }
        return [...m.values()].sort((a, b) => String(a.recipe_name || a.recipe_key).localeCompare(String(b.recipe_name || b.recipe_key), "ar"));
    }, [rows]);

    const counts = useMemo(() => {
        const c = { all: rows.length };
        for (const f of FILTERS.slice(1)) c[f] = rows.filter((r) => inFilter(r, f)).length;
        c.products = rows.filter((r) => !isRawItem(r.item)).length;
        const never = new Set(meta.never);
        c.bulk = rows.filter((r) => eligibleForBulk(r, never)).length;
        return c;
    }, [rows, meta.never]);

    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return rows.filter((r) => inFilter(r, filter) && (!needle || [
            r.item?.code, r.item?.name_ar, r.item?.name_en, r.link?.recipe_name, r.suggestion?.recipe_name,
        ].some((v) => String(v || "").toLowerCase().includes(needle))));
    }, [rows, q, filter]);

    const num = (v) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(v || 0);

    const runBulk = async () => {
        setBulkBusy(true);
        try {
            const res = await api.post("/waste-meals/admin/recipe-links/confirm-all", {}, { timeout: WM_TIMEOUT_MS });
            const v = res.data?.linked ?? res.data?.count ?? res.data?.n;
            const n = Array.isArray(v) ? v.length : (typeof v === "number" ? v : counts.bulk);
            toast.success(fmt(t("wm_links_confirm_all_done"), { n: num(n) }));
            setBulkOpen(false);
            await load();
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div className="space-y-3">
            <p className="text-xs leading-relaxed text-[color:var(--ink-muted)]">{t("wm_links_hint")}</p>
            {meta.recipesN === 0 ? (
                // preview has no recipes: every suggestion is «لا توجد مطابقة» until the POS ingests them
                <p className="flex items-start gap-1.5 rounded-[var(--radius-sm)] bg-[color:var(--warning-soft)] px-3 py-2 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t("wm_links_no_recipes")}
                </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-full sm:w-56">
                    <Search className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-[color:var(--ink-faint)]" />
                    <Input data-testid={C.linkSearch} value={q} onChange={(e) => setQ(e.target.value)}
                           placeholder={t("wm_admin_search")} className="h-9 w-full ps-8 text-base sm:text-sm" />
                </div>
                <div className="flex flex-wrap gap-1 rounded-full border border-[color:var(--border-soft)] p-0.5">
                    {FILTERS.map((f) => (
                        <button key={f} type="button" data-testid={C.linkFilter(f)} data-active={filter === f ? "true" : "false"}
                                onClick={() => setFilter(f)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                    filter === f
                                        ? "bg-[color:var(--brand-orange)] text-white"
                                        : "text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
                                }`}>
                            {t(`wm_links_filter_${f}`)} <bdi dir="ltr" className="opacity-80">{num(counts[f])}</bdi>
                        </button>
                    ))}
                </div>
                <Badge data-testid={C.linksSummary} variant="secondary" className="h-7 px-2.5 text-[11px]">
                    {t("wm_links_state_linked")}: <bdi dir="ltr" className="ms-1">{num(counts.linked)}/{num(counts.products)}</bdi>
                </Badge>
                <Button type="button" variant="ghost" size="sm" onClick={load} disabled={loading}
                        aria-label={t("wm_links_refresh")} className="h-9 w-9 p-0">
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <Button type="button" data-testid={C.linkConfirmAll} size="sm" onClick={() => setBulkOpen(true)}
                        disabled={!counts.bulk || loading}
                        className="h-9 w-full gap-1.5 text-xs font-semibold sm:ms-auto sm:w-auto">
                    <CheckCheck className="h-4 w-4" />
                    {t("wm_links_confirm_all")} (<bdi dir="ltr">{num(counts.bulk)}</bdi>)
                </Button>
            </div>

            {failed ? (
                <div data-testid={OPS.wm.offlineError}
                     className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                        <WifiOff className="h-3.5 w-3.5" />{t("wm_offline_error")}
                    </span>
                    <Button size="sm" variant="outline" data-testid={OPS.wm.retry} onClick={load}>{t("wm_retry")}</Button>
                </div>
            ) : null}

            {loading && !rows.length ? <InlineLoader /> : !shown.length ? (
                <p data-testid={C.linksEmpty}
                   className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                    {t(rows.length ? "wm_links_empty" : "wm_links_none")}
                </p>
            ) : (
                <ul data-testid={C.linksTable}
                    className={`grid gap-2 transition-opacity duration-200 lg:grid-cols-2 ${loading ? "opacity-60" : ""}`}>
                    {shown.map((r) => (
                        // remount on a server change so the row's edit state starts from the saved link
                        <LinkRow key={`${r.item.id}:${r.link?.recipe_key || ""}:${r.link?.qty_factor ?? ""}:${r.link?.linked_at || ""}`}
                                 row={r} pool={pool} costsVisible={meta.costsVisible} onSaved={onSaved} />
                    ))}
                </ul>
            )}

            <AlertDialog open={bulkOpen} onOpenChange={(v) => { if (!bulkBusy) setBulkOpen(v); }}>
                <AlertDialogContent data-testid={C.linkConfirmAllDialog}>
                    <AlertDialogHeader className="text-start sm:text-start">
                        <AlertDialogTitle>{t("wm_links_confirm_all")}</AlertDialogTitle>
                        <AlertDialogDescription className="leading-relaxed">
                            {fmt(t("wm_links_confirm_all_desc"), { n: num(counts.bulk) })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={bulkBusy}>{t("wm_cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={C.linkConfirmAllRun} disabled={bulkBusy}
                                           onClick={(e) => { e.preventDefault(); runBulk(); }}>
                            {bulkBusy ? t("wm_saving") : t("wm_links_confirm_all_run")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
